import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { getDvSourceImageUrl, ensureThemeJsonArray } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./game.css";
import Swal from "sweetalert2";
import pointIcon from "./img/point.png";

const SHAKE_MS = 520;
const POINTS_GLOW_MS = 700;
const MAX_PLAYER_ANSWER_LEN = 25;
const CLOUD_COLORS = ["#ffffff", "#ffc48a", "#ff6b5a", "#ff3b30", "#ffe8dc"];
const CLOUD_SLOTS = [
  { left: 50, top: 18, rot: -4, scale: 1.35 },
  { left: 28, top: 32, rot: 8, scale: 1.05 },
  { left: 72, top: 28, rot: -10, scale: 1.15 },
  { left: 18, top: 48, rot: 6, scale: 0.92 },
  { left: 82, top: 44, rot: -6, scale: 0.98 },
  { left: 38, top: 58, rot: -12, scale: 1.22 },
  { left: 62, top: 54, rot: 14, scale: 1.08 },
  { left: 50, top: 68, rot: 0, scale: 1.28 },
  { left: 24, top: 72, rot: 10, scale: 0.88 },
  { left: 76, top: 66, rot: -8, scale: 0.95 },
  { left: 44, top: 38, rot: 4, scale: 0.9 },
  { left: 56, top: 42, rot: -14, scale: 1.12 },
];

function themeColorToCss(val) {
  if (val == null || val === "") return undefined;
  const c = String(val).trim();
  return c || undefined;
}

function normalizeWordKey(word) {
  return String(word ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Letter count for scoring (ignores spaces). */
function countWordLetters(word) {
  return String(word ?? "")
    .trim()
    .replace(/\s+/g, "").length;
}

const BASE_WORD_LENGTH = 3;
const EXTRA_LETTER_POINTS_DEFAULT = 5;

/** themeData.points = score for a 3-letter word; +5 per letter beyond 3. */
function computeAnswerPoints(word, basePoints, extraPerLetter = EXTRA_LETTER_POINTS_DEFAULT) {
  const base = Number.isFinite(basePoints) && basePoints > 0 ? basePoints : 1;
  const extra = Number.isFinite(extraPerLetter) && extraPerLetter >= 0 ? extraPerLetter : EXTRA_LETTER_POINTS_DEFAULT;
  const len = countWordLetters(word);
  if (len <= BASE_WORD_LENGTH) return base;
  return base + (len - BASE_WORD_LENGTH) * extra;
}

function parseClockToSeconds(clock) {
  const s = String(clock ?? "").trim();
  if (!s) return 0;
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/** Seconds left until a wall-clock deadline (ceil so HUD shows full seconds). */
function remainingSecUntil(deadlineMs) {
  if (deadlineMs == null) return 0;
  return Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

async function callAI(prompt, token) {
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!backendBase) throw new Error("Backend URL is not configured.");
  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");
  return payload?.response;
}

function buildWordValidatePrompt(topicName, questionName, acceptableWords, userWord, alreadyFound) {
  const topic = topicName ? `Topic/category: "${topicName}"` : "";
  const found =
    alreadyFound?.length > 0
      ? `Already found by player (do not accept duplicates of these): ${JSON.stringify(alreadyFound)}`
      : "";
  return `You validate answers for a category word-guessing game.

${topic}
Question: "${questionName}"
Example answers from the game (partial hints only — NOT a complete list): ${JSON.stringify(acceptableWords)}
Player typed: "${userWord}"
${found}

Decide if the player's answer is CORRECT for this question.

Say YES only when:
- The answer is a correctly spelled real English word or common term that fits the question (standard dictionary spelling).
- It is a valid fit even if NOT in the example list (e.g. "mouse" for office items when not listed).
- Synonyms, plurals, and well-known abbreviations are OK only if spelled correctly.

Say NO when:
- Too short to be a real word (fewer than 3 letters, e.g. "aa", "tt", "9", "99").
- Keyboard mashing on one row (e.g. "tyu", "ghj", "asd", "qwe") — not real words.
- Misspelled words (e.g. "scaner" when the correct spelling is "scanner") — spelling must be correct.
- Gibberish, random keystrokes, codes, or strings with digits (e.g. "e876", "abc123").
- Fake words, keyboard mashing, or meaningless letter/number mixes.
- The answer is wrong or unrelated to the question.
- Duplicate of an already-found answer.
- You are not sure it is a genuine correctly spelled word for this category — be strict; when in doubt say NO.

Do NOT accept near-miss spellings or answers that only sound similar to a real word.

Reply with exactly one word: YES or NO. No other text.`;
}

function parseAiYesNo(responseText) {
  const clean = String(responseText || "").trim().toUpperCase();
  if (!clean) return false;
  if (/^\s*YES\b/.test(clean) || clean === "Y") return true;
  if (/^\s*NO\b/.test(clean) || clean === "N") return false;
  const firstToken = clean.split(/\s+/)[0];
  if (firstToken === "YES" || firstToken === "Y") return true;
  if (firstToken === "NO" || firstToken === "N") return false;
  if (/\bYES\b/.test(clean) && !/\bNO\b/.test(clean)) return true;
  return false;
}

function levenshtein(a, b) {
  const s = String(a ?? "");
  const t = String(b ?? "");
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const rows = s.length + 1;
  const cols = t.length + 1;
  const d = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 0; i < rows; i++) d[i][0] = i;
  for (let j = 0; j < cols; j++) d[0][j] = j;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
    }
  }
  return d[rows - 1][cols - 1];
}

/** Close to a list word but not exact — likely a typo (e.g. scaner vs scanner). */
function isLikelyMisspellingOfListWord(typed, words) {
  const key = normalizeWordKey(typed);
  if (!key || !Array.isArray(words)) return false;
  if (words.some((w) => normalizeWordKey(w) === key)) return false;
  for (const w of words) {
    const wk = normalizeWordKey(w);
    if (!wk || wk === key) continue;
    const dist = levenshtein(key, wk);
    const lenDiff = Math.abs(key.length - wk.length);
    if (dist > 0 && dist <= 2 && lenDiff <= 2) return true;
  }
  return false;
}

const MIN_ANSWER_LETTERS = 3;

/** QWERTY rows — random key mashing often stays on one row (tyu, ghj, asd). */
const KEYBOARD_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

function isKeyboardRowMash(word) {
  const w = String(word ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (w.length < 3) return false;
  return KEYBOARD_ROWS.some((row) => [...w].every((ch) => row.includes(ch)));
}

/** Reject keyboard-mash / nonsense before calling AI (Latin letters only heuristic). */
function tokenLooksLikeGibberish(token) {
  const raw = String(token ?? "").trim();
  if (!raw) return true;
  if (/^\d+$/.test(raw)) return true;

  const t = raw.replace(/[^a-z]/gi, "");
  if (t.length < MIN_ANSWER_LETTERS) return true;

  if (/\d/.test(raw)) return true;

  if (isKeyboardRowMash(t)) return true;

  const vowels = (t.match(/[aeiouy]/gi) || []).length;
  if (vowels === 0) return true;

  if (t.length <= 4 && !/[aeiouy]/i.test(t)) return true;

  if (/^(.)\1+$/i.test(t) && t.length <= 3) return true;

  if (t.length >= 4 && vowels === 0) return true;

  let maxConsonantRun = 0;
  let run = 0;
  for (const ch of t) {
    if (/[bcdfghjklmnpqrstvwxz]/i.test(ch)) {
      run += 1;
      maxConsonantRun = Math.max(maxConsonantRun, run);
    } else {
      run = 0;
    }
  }
  if (t.length <= 5 && maxConsonantRun >= 3) return true;
  if (maxConsonantRun >= 5) return true;

  if (t.length <= 4 && vowels / t.length < 0.34) return true;

  if (t.length >= 5) {
    if (vowels / t.length < 0.18) return true;
  }

  return false;
}

function answerLooksInvalid(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return true;

  const compact = raw.replace(/\s+/g, "");
  if (/^\d+$/.test(compact)) return true;
  if (/\d/.test(raw)) return true;

  const letterCount = countWordLetters(raw);
  if (letterCount > 0 && letterCount < MIN_ANSWER_LETTERS) return true;

  const parts = raw.split(/\s+/).filter(Boolean);
  for (const p of parts) {
    if (tokenLooksLikeGibberish(p)) return true;
  }
  if (/^[a-z]*\d+[a-z0-9]*$/i.test(compact)) return true;
  return false;
}

const WELCOME_STAGE_SESSION_KEY = "category_rush_welcome_stage_state";

function readWelcomeStageSession() {
  try {
    const raw = sessionStorage.getItem(WELCOME_STAGE_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getCachedQuestionIndex(payload) {
  if (!payload) return 0;
  const q = Number(payload.activeQuestionIndex);
  return Number.isFinite(q) && q >= 0 ? Math.floor(q) : 0;
}

function getCachedTimeLeftSec(payload) {
  if (!payload?.questionTimerReady) return 0;
  const paused = payload.questionTimerPausedMs;
  if (paused != null && Number.isFinite(Number(paused))) {
    return Math.max(0, Math.ceil(Number(paused) / 1000));
  }
  const end = payload.questionEndsAtMs;
  if (end != null && Number.isFinite(Number(end))) {
    return remainingSecUntil(Number(end));
  }
  return 0;
}

/** Resume wall-clock deadline after restore when timer was paused (e.g. during AI check). */
function reactivateQuestionTimerDeadline(endsAtRef, pausedMsRef, readyRef) {
  if (!readyRef.current) return;
  if (endsAtRef.current == null && pausedMsRef.current != null) {
    endsAtRef.current = Date.now() + pausedMsRef.current;
    pausedMsRef.current = null;
  }
}

function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus, currentTheme } = useSelector((state) => state.theme);

  const bootSessionRef = useRef(null);
  if (bootSessionRef.current === null) {
    bootSessionRef.current = readWelcomeStageSession();
  }
  const bootSession = bootSessionRef.current;

  const [points, setPoints] = useState(() => {
    const p = Number(bootSession?.points ?? 0);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [wrongCount, setWrongCount] = useState(() => {
    const w = Number(bootSession?.wrongCount ?? 0);
    return Number.isFinite(w) && w >= 0 ? w : 0;
  });
  const [timeLeft, setTimeLeft] = useState(() => getCachedTimeLeftSec(bootSession));
  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(() => getCachedQuestionIndex(bootSession));
  const [foundWords, setFoundWords] = useState(() =>
    Array.isArray(bootSession?.foundWords) ? bootSession.foundWords : []
  );
  const [inputValue, setInputValue] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [showRulesPopup, setShowRulesPopup] = useState(false);
  const [flyingWord, setFlyingWord] = useState(null);
  const [pointsGlowing, setPointsGlowing] = useState(false);

  const headerSet = useRef(false);
  const inputRef = useRef(null);
  const cloudRef = useRef(null);
  const flyingWordRef = useRef(null);
  const initialPoints = (() => {
    const p = Number(bootSession?.points ?? 0);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  })();
  const initialWrongCount = (() => {
    const w = Number(bootSession?.wrongCount ?? 0);
    return Number.isFinite(w) && w >= 0 ? w : 0;
  })();
  const pointsRef = useRef(initialPoints);
  const wrongCountRef = useRef(initialWrongCount);
  const timeLeftRef = useRef(getCachedTimeLeftSec(bootSession));
  const elapsedTimeRef = useRef(0);
  const sessionTimeSecRef = useRef(0);
  const questionsCreditedRef = useRef(0);
  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);
  const shakeTimerRef = useRef(null);
  const pointsGlowTimerRef = useRef(null);
  const gameCompleteRef = useRef(false);
  const configAlertShownRef = useRef(false);
  const questionTimerReadyRef = useRef(Boolean(bootSession?.questionTimerReady));
  const questionEndsAtMsRef = useRef(
    bootSession?.questionEndsAtMs != null && Number.isFinite(Number(bootSession.questionEndsAtMs))
      ? Number(bootSession.questionEndsAtMs)
      : null
  );
  const questionTimerPausedMsRef = useRef(
    bootSession?.questionTimerPausedMs != null && Number.isFinite(Number(bootSession.questionTimerPausedMs))
      ? Number(bootSession.questionTimerPausedMs)
      : null
  );
  const lastQuestionTimeLeftRef = useRef(0);
  const questionTimerEpochRef = useRef(0);
  const advanceQuestionOrFinishRef = useRef(() => {});
  const questionAdvanceTimerRef = useRef(null);
  const questionAdvanceFromZeroTimerRef = useRef(null);
  const advanceInFlightRef = useRef(false);
  const questionsListLengthRef = useRef(0);
  const questionsInitializedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);
  const showRulesPopupRef = useRef(false);
  showRulesPopupRef.current = showRulesPopup;
  const activeQuestionIndexRef = useRef(activeQuestionIndex);
  activeQuestionIndexRef.current = activeQuestionIndex;
  const isValidatingRef = useRef(false);

  const foundWordsRef = useRef(Array.isArray(bootSession?.foundWords) ? bootSession.foundWords : []);
  const restoreFromSessionPendingRef = useRef(false);
  const restorePayloadRef = useRef(null);
  const cachedSessionPayloadRef = useRef(bootSession);
  const prevQuestionIndexRef = useRef(getCachedQuestionIndex(bootSession));
  const sessionHydratedFromCacheRef = useRef(Boolean(bootSession));
  const sessionReadyToPersistRef = useRef(!bootSession);

  const storedUserData = JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  const sid = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oid = String(storedUserData?.organizationId ?? "").toLowerCase();
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sid === "demobypass" && oid === "demobypass");

  const questionsList = useMemo(() => {
    let raw = ensureThemeJsonArray(themeData?.questions);
    if (
      !raw.length &&
      themeData?.questions &&
      typeof themeData.questions === "object" &&
      !Array.isArray(themeData.questions)
    ) {
      raw = Object.values(themeData.questions);
    }
    return raw
      .filter((q) => q && typeof q === "object" && q.deleted !== true)
      .map((q) => ({
        topic_name: String(q?.topic_name ?? "").trim(),
        question_name: String(q?.question_name ?? "").trim(),
        words: ensureThemeJsonArray(q?.words)
          .map((w) => String(w ?? "").trim())
          .filter(Boolean),
      }))
      .filter((q) => q.question_name && q.words.length > 0);
  }, [themeData]);

  const hasNoQuestions = themeData && questionsList.length === 0;
  questionsListLengthRef.current = questionsList.length;
  const activeQuestion = questionsList[activeQuestionIndex] || null;
  const adminRightPoints = parseInt(themeData?.points, 10) || 1;
  const questionTimerSec = parseClockToSeconds(themeData?.timer) || 60;
  const perQuestionTimer = questionTimerSec > 0;

  const timerLineProgress = useMemo(() => {
    if (!perQuestionTimer || questionTimerSec <= 0) return 1;
    return Math.max(0, Math.min(1, timeLeft / questionTimerSec));
  }, [perQuestionTimer, questionTimerSec, timeLeft]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  const lockedGamePath = useMemo(() => {
    const p = String(location.pathname || "/welcome");
    const s = String(location.search || "");
    const h = String(location.hash || "");
    return `${p}${s}${h}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (headerSet.current) return;
    dispatch(setBackButtonUrl(lockedGamePath || "/welcome"));
    headerSet.current = true;
  }, [dispatch, lockedGamePath]);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);
  useEffect(() => {
    wrongCountRef.current = wrongCount;
  }, [wrongCount]);
  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);

  useEffect(() => {
    gameCompleteRef.current = gameComplete;
  }, [gameComplete]);

  useEffect(() => {
    isValidatingRef.current = isValidating;
  }, [isValidating]);

  useEffect(() => {
    foundWordsRef.current = foundWords;
  }, [foundWords]);

  const persistWelcomeSessionState = useCallback(() => {
    if (gameCompleteRef.current) return;
    if (!sessionReadyToPersistRef.current) return;
    if (!perQuestionTimer) return;
    if (!questionTimerReadyRef.current) return;
    try {
      sessionStorage.setItem(
        WELCOME_STAGE_SESSION_KEY,
        JSON.stringify({
          activeQuestionIndex: activeQuestionIndexRef.current,
          foundWords: foundWordsRef.current,
          points: pointsRef.current,
          wrongCount: wrongCountRef.current,
          questionEndsAtMs: questionEndsAtMsRef.current,
          questionTimerPausedMs: questionTimerPausedMsRef.current,
          questionTimerReady: questionTimerReadyRef.current,
          savedAt: Date.now(),
        })
      );
    } catch {
      /* ignore */
    }
  }, [perQuestionTimer]);

  useEffect(() => {
    persistWelcomeSessionState();
  }, [foundWords, points, wrongCount, persistWelcomeSessionState]);

  useEffect(() => {
    if (!gameComplete) {
      return;
    }
    try {
      sessionStorage.removeItem(WELCOME_STAGE_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, [gameComplete]);

  const getQuestionTimeLeftSec = useCallback(() => {
    const pausedMs = questionTimerPausedMsRef.current;
    if (pausedMs != null) return Math.max(0, Math.ceil(pausedMs / 1000));
    return remainingSecUntil(questionEndsAtMsRef.current);
  }, []);

  const syncQuestionTimeDisplay = useCallback(() => {
    if (!perQuestionTimer || !questionTimerReadyRef.current) return 0;
    const left = getQuestionTimeLeftSec();
    timeLeftRef.current = left;
    setTimeLeft(left);
    return left;
  }, [perQuestionTimer, getQuestionTimeLeftSec]);

  const stopQuestionTimer = useCallback(() => {
    questionTimerReadyRef.current = false;
    questionEndsAtMsRef.current = null;
    questionTimerPausedMsRef.current = null;
  }, []);

  const startQuestionTimer = useCallback(() => {
    questionTimerEpochRef.current += 1;
    questionTimerPausedMsRef.current = null;
    if (!perQuestionTimer || questionTimerSec <= 0) {
      questionEndsAtMsRef.current = null;
      questionTimerReadyRef.current = false;
      timeLeftRef.current = 0;
      setTimeLeft(0);
      return;
    }
    questionEndsAtMsRef.current = Date.now() + questionTimerSec * 1000;
    questionTimerReadyRef.current = true;
    const left = remainingSecUntil(questionEndsAtMsRef.current);
    timeLeftRef.current = left;
    setTimeLeft(left);
    persistWelcomeSessionState();
  }, [perQuestionTimer, questionTimerSec, persistWelcomeSessionState]);

  const pauseQuestionTimer = useCallback(() => {
    if (!questionTimerReadyRef.current || !perQuestionTimer) return;
    if (questionTimerPausedMsRef.current != null) return;
    const end = questionEndsAtMsRef.current;
    if (end == null) return;
    questionTimerPausedMsRef.current = Math.max(0, end - Date.now());
    questionEndsAtMsRef.current = null;
    syncQuestionTimeDisplay();
    persistWelcomeSessionState();
  }, [perQuestionTimer, syncQuestionTimeDisplay, persistWelcomeSessionState]);

  const resumeQuestionTimer = useCallback(() => {
    if (!questionTimerReadyRef.current || !perQuestionTimer) return;
    const pausedMs = questionTimerPausedMsRef.current;
    if (pausedMs == null) return;
    questionEndsAtMsRef.current = Date.now() + pausedMs;
    questionTimerPausedMsRef.current = null;
    syncQuestionTimeDisplay();
    persistWelcomeSessionState();
  }, [perQuestionTimer, syncQuestionTimeDisplay, persistWelcomeSessionState]);

  useEffect(() => {
    if (!perQuestionTimer) return;
    if (isValidating) pauseQuestionTimer();
    else resumeQuestionTimer();
  }, [isValidating, perQuestionTimer, pauseQuestionTimer, resumeQuestionTimer]);

  const scheduleAnswerInputFocus = useCallback(() => {
    if (gameCompleteRef.current || showRulesPopupRef.current) return;
    const run = () => {
      if (gameCompleteRef.current || showRulesPopupRef.current) return;
      const el = inputRef.current;
      if (!el || el.disabled) return;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }, []);

  useEffect(() => {
    if (gameComplete || showRulesPopup) return;
    if (flyingWord || isValidating) return;
    scheduleAnswerInputFocus();
  }, [
    flyingWord,
    isValidating,
    gameComplete,
    showRulesPopup,
    activeQuestionIndex,
    scheduleAnswerInputFocus,
  ]);

  useEffect(() => {
    const body = document.body;
    body.classList.add("game-active");
    body.style.setProperty("backdrop-filter", "none");
    return () => {
      body.classList.remove("game-active");
      body.style.removeProperty("backdrop-filter");
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      if (pointsGlowTimerRef.current) clearTimeout(pointsGlowTimerRef.current);
      if (questionAdvanceTimerRef.current) clearTimeout(questionAdvanceTimerRef.current);
      if (questionAdvanceFromZeroTimerRef.current)
        clearTimeout(questionAdvanceFromZeroTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "failed") navigate("/login", { replace: true });
  }, [themeStatus, navigate]);

  useEffect(() => {
    if (!themeData || !hasNoQuestions) {
      configAlertShownRef.current = false;
      return;
    }
    if (configAlertShownRef.current) return;
    configAlertShownRef.current = true;
    Swal.fire({
      icon: "warning",
      title: "Questions missing",
      text: "Please add questions in Theme Update before starting the game.",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      const baseUrl = String(process.env.REACT_APP_BASE_URL || "").trim();
      window.location.assign(baseUrl || "/");
    });
  }, [themeData, hasNoQuestions]);

  useEffect(() => {
    if (!themeData || gameComplete || hasNoQuestions) return;
    const tick = () => {
      setElapsedTime((p) => {
        const next = p + 1;
        elapsedTimeRef.current = next;
        return next;
      });
      if (!perQuestionTimer || !questionTimerReadyRef.current) return;

      const epoch = questionTimerEpochRef.current;
      const left = syncQuestionTimeDisplay();
      if (
        left <= 0 &&
        !isValidatingRef.current &&
        questionTimerReadyRef.current &&
        questionTimerEpochRef.current === epoch
      ) {
        questionTimerReadyRef.current = false;
        questionEndsAtMsRef.current = null;
        if (questionAdvanceFromZeroTimerRef.current) {
          clearTimeout(questionAdvanceFromZeroTimerRef.current);
          questionAdvanceFromZeroTimerRef.current = null;
        }
        // Allow a render with `0` before moving to the next question.
        questionAdvanceFromZeroTimerRef.current = window.setTimeout(() => {
          questionAdvanceFromZeroTimerRef.current = null;
          advanceQuestionOrFinishRef.current();
        }, 180);
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [themeData, gameComplete, hasNoQuestions, perQuestionTimer, syncQuestionTimeDisplay]);

  useEffect(() => {
    const len = questionsList.length;
    if (!len) return;
    if (!questionsInitializedRef.current) {
      questionsInitializedRef.current = true;
      const cachedIdx = getCachedQuestionIndex(cachedSessionPayloadRef.current);
      const clamped = Math.min(Math.max(0, cachedIdx), len - 1);
      if (clamped !== activeQuestionIndexRef.current) {
        setActiveQuestionIndex(clamped);
        activeQuestionIndexRef.current = clamped;
        prevQuestionIndexRef.current = clamped;
      }
      return;
    }
    setActiveQuestionIndex((prev) => Math.min(Math.max(0, prev), len - 1));
  }, [questionsList.length]);

  useEffect(() => {
    advanceInFlightRef.current = false;
    if (questionAdvanceTimerRef.current) {
      clearTimeout(questionAdvanceTimerRef.current);
      questionAdvanceTimerRef.current = null;
    }
    const midFly = flyingWordRef.current;
    if (midFly && Number.isFinite(midFly.earned) && midFly.earned > 0) {
      setPoints((p) => {
        const next = Math.max(0, p - midFly.earned);
        pointsRef.current = next;
        return next;
      });
    }
    flyingWordRef.current = null;
    setFlyingWord(null);

    const prevIndex = prevQuestionIndexRef.current;
    const isActualQuestionChange = prevIndex !== activeQuestionIndex;
    prevQuestionIndexRef.current = activeQuestionIndex;

    const shouldRestore =
      restoreFromSessionPendingRef.current &&
      restorePayloadRef.current &&
      restorePayloadRef.current.activeQuestionIndex === activeQuestionIndex;

    if (shouldRestore) {
      const payload = restorePayloadRef.current;
      restoreFromSessionPendingRef.current = false;
      restorePayloadRef.current = null;

      setFoundWords(Array.isArray(payload.foundWords) ? payload.foundWords : []);
      setInputValue(typeof payload.inputValue === "string" ? payload.inputValue : "");

      const savedPoints = Number(payload.points ?? 0);
      const savedWrongCount = Number(payload.wrongCount ?? 0);
      if (Number.isFinite(savedPoints) && savedPoints >= 0) {
        setPoints((p) => {
          const next = Math.max(p, savedPoints);
          pointsRef.current = next;
          return next;
        });
      }
      if (Number.isFinite(savedWrongCount) && savedWrongCount >= 0) {
        setWrongCount((w) => {
          const next = Math.max(w, savedWrongCount);
          wrongCountRef.current = next;
          return next;
        });
      }

      questionEndsAtMsRef.current = payload.questionEndsAtMs ?? null;
      questionTimerPausedMsRef.current = payload.questionTimerPausedMs ?? null;
      questionTimerReadyRef.current = Boolean(payload.questionTimerReady) && perQuestionTimer;

      reactivateQuestionTimerDeadline(
        questionEndsAtMsRef,
        questionTimerPausedMsRef,
        questionTimerReadyRef
      );

      if (questionTimerReadyRef.current) {
        syncQuestionTimeDisplay();
      } else {
        startQuestionTimer();
      }
      sessionHydratedFromCacheRef.current = true;
      sessionReadyToPersistRef.current = true;
      return;
    }

    // Already hydrated from sessionStorage on mount — don't wipe words/timer.
    if (
      !isActualQuestionChange &&
      sessionHydratedFromCacheRef.current &&
      getCachedQuestionIndex(cachedSessionPayloadRef.current) === activeQuestionIndex
    ) {
      reactivateQuestionTimerDeadline(
        questionEndsAtMsRef,
        questionTimerPausedMsRef,
        questionTimerReadyRef
      );
      if (questionTimerReadyRef.current) {
        syncQuestionTimeDisplay();
      }
      sessionReadyToPersistRef.current = true;
      return;
    }

    if (!isActualQuestionChange) {
      if (perQuestionTimer && !questionTimerReadyRef.current) {
        startQuestionTimer();
      }
      sessionReadyToPersistRef.current = true;
      return;
    }

    sessionHydratedFromCacheRef.current = false;
    setFoundWords([]);
    setInputValue("");
    startQuestionTimer();
  }, [activeQuestionIndex, startQuestionTimer, perQuestionTimer, syncQuestionTimeDisplay]);

  useEffect(() => {
    if (!storedUserData?.sessionId || isDemoBypass) return;
    const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    if (!uid || !sessionId || !organizationId || !adminToken) return;

    const syncReportAndMaybeRedirect = async () => {
      const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      if (!backendBase) return;
      const playerName = normalizePlayerName(storedUserData);

      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) return;

        if (data.report) {
          if (Number(data.report.gameover) === 1) {
            navigate("/leaderboard", {
              replace: true,
              state: {
                points: Number(data.report.points) || 0,
                time: data.report.time ?? "00:00",
              },
            });
            return;
          }

          const stageId = data.report.id;
          if (stageId != null && stageId !== "") stageRowIdRef.current = String(stageId);

          const savedCurrentQuesRaw = Number(
            data.report.currentQues ?? data.report.current_ques ?? data.report.currentQuesCount ?? 0
          );
          const savedCurrentQuesSafe =
            Number.isFinite(savedCurrentQuesRaw) && savedCurrentQuesRaw >= 0 ? Math.floor(savedCurrentQuesRaw) : 0;

          const rp = Number(data.report.points);
          if (Number.isFinite(rp)) {
            setPoints(rp);
            pointsRef.current = rp;
          }
          const rw = Number(data.report.wrongcount);
          if (Number.isFinite(rw)) {
            setWrongCount(rw);
            wrongCountRef.current = rw;
          }
          if (perQuestionTimer) {
            const len = questionsListLengthRef.current || 0;
            const dbTimeSec = parseClockToSeconds(data.report.time ?? "00:00");
            const completedFromTime =
              questionTimerSec > 0 ? Math.floor(dbTimeSec / questionTimerSec) : savedCurrentQuesSafe;

            const targetQIndexRaw = Number.isFinite(completedFromTime) ? completedFromTime : savedCurrentQuesSafe;
            let clampedQIndex = len > 0 ? Math.min(Math.max(0, targetQIndexRaw), len - 1) : 0;

            sessionTimeSecRef.current = clampedQIndex * questionTimerSec;
            questionsCreditedRef.current = clampedQIndex;

            const cachedPayload = cachedSessionPayloadRef.current;
            const cachedIdxRaw = cachedPayload?.activeQuestionIndex;
            const cachedIdx =
              Number.isFinite(Number(cachedIdxRaw)) && Number(cachedIdxRaw) >= 0 ? Math.floor(Number(cachedIdxRaw)) : null;

            // If backend progress is behind but our in-browser timer deadline is still in the future,
            // prefer resuming the question the player was actually on.
            const cachedEndsAtMsRaw = cachedPayload?.questionEndsAtMs;
            const cachedEndsAtMs =
              cachedEndsAtMsRaw != null && Number.isFinite(Number(cachedEndsAtMsRaw)) ? Number(cachedEndsAtMsRaw) : null;
            const cachedPausedMsRaw = cachedPayload?.questionTimerPausedMs;
            const cachedPausedMs =
              cachedPausedMsRaw != null && Number.isFinite(Number(cachedPausedMsRaw)) ? Number(cachedPausedMsRaw) : null;
            const cachedRemainingSec =
              cachedPausedMs != null
                ? Math.max(0, Math.ceil(cachedPausedMs / 1000))
                : cachedEndsAtMs != null
                  ? remainingSecUntil(cachedEndsAtMs)
                  : null;

            const cachedIdxClamped =
              cachedIdx == null
                ? null
                : len > 0
                  ? Math.min(Math.max(0, cachedIdx), len - 1)
                  : 0;

            const shouldPreferCachedResume =
              cachedPayload &&
              cachedIdxClamped != null &&
              cachedIdxClamped >= clampedQIndex &&
              ((cachedRemainingSec != null && cachedRemainingSec > 0) ||
                (Array.isArray(cachedPayload.foundWords) && cachedPayload.foundWords.length > 0));

            if (shouldPreferCachedResume) {
              clampedQIndex = cachedIdxClamped;
              sessionTimeSecRef.current = clampedQIndex * questionTimerSec;
              questionsCreditedRef.current = clampedQIndex;
              restoreFromSessionPendingRef.current = true;
              restorePayloadRef.current = {
                ...cachedPayload,
                activeQuestionIndex: clampedQIndex,
              };
            } else if (cachedPayload && cachedIdx === clampedQIndex) {
              restoreFromSessionPendingRef.current = true;
              restorePayloadRef.current = cachedPayload;
            } else {
              restoreFromSessionPendingRef.current = false;
              restorePayloadRef.current = null;
            }

            setActiveQuestionIndex(clampedQIndex);
            activeQuestionIndexRef.current = clampedQIndex;

            // If we're restoring the same index as initial mount (e.g. Q0), React may not re-run
            // the [activeQuestionIndex] effect, so apply the cached restore immediately.
            if (
              restoreFromSessionPendingRef.current &&
              restorePayloadRef.current &&
              restorePayloadRef.current.activeQuestionIndex === clampedQIndex
            ) {
              const payload = restorePayloadRef.current;
              restoreFromSessionPendingRef.current = false;
              restorePayloadRef.current = null;

              setFoundWords(Array.isArray(payload.foundWords) ? payload.foundWords : []);
              setInputValue(typeof payload.inputValue === "string" ? payload.inputValue : "");

              const savedPoints = Number(payload.points ?? 0);
              const savedWrongCount = Number(payload.wrongCount ?? 0);
              if (Number.isFinite(savedPoints) && savedPoints >= 0) {
                setPoints((p) => {
                  const next = Math.max(p, savedPoints);
                  pointsRef.current = next;
                  return next;
                });
              }
              if (Number.isFinite(savedWrongCount) && savedWrongCount >= 0) {
                setWrongCount((w) => {
                  const next = Math.max(w, savedWrongCount);
                  wrongCountRef.current = next;
                  return next;
                });
              }

              questionEndsAtMsRef.current = payload.questionEndsAtMs ?? null;
              questionTimerPausedMsRef.current = payload.questionTimerPausedMs ?? null;
              questionTimerReadyRef.current = Boolean(payload.questionTimerReady) && perQuestionTimer;

              reactivateQuestionTimerDeadline(
                questionEndsAtMsRef,
                questionTimerPausedMsRef,
                questionTimerReadyRef
              );

              if (questionTimerReadyRef.current) {
                syncQuestionTimeDisplay();
              } else {
                startQuestionTimer();
              }

              sessionHydratedFromCacheRef.current = true;
              sessionReadyToPersistRef.current = true;
              prevQuestionIndexRef.current = clampedQIndex;
            }

            if (stageRowIdRef.current && !isDemoBypass) {
              persistWelcomeStageProgress(clampedQIndex);
            }
          } else {
            setActiveQuestionIndex(0);
            activeQuestionIndexRef.current = 0;
          }

          const extRid = data.report.reportId;
          if (extRid != null && extRid !== "") {
            reportIdRef.current = String(extRid);
            return;
          }
        } else {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: playerName,
              user_type: reportRoleForApi,
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            stageRowIdRef.current = String(startData.id);
            if (!isDemoBypass) {
              setActiveQuestionIndex(0);
              activeQuestionIndexRef.current = 0;
              await persistWelcomeStageProgress(0);
            }
          }
        }

        if (reportIdRef.current) return;

        const seedRes = await sendReport({
          sessionId,
          organizationId,
          role: reportRoleForApi,
          token: storedUserData.token || "",
          points: 0,
          time: "00:00",
          gameId: storedUserData.gameId,
          name: playerName,
          userId: uid,
        });
        const newRid = extractReportIdFromResponse(seedRes);
        if (newRid != null && newRid !== "" && stageRowIdRef.current) {
          reportIdRef.current = String(newRid);
          try {
            await fetch(`${backendBase}/welcomeStageLinkReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId,
                organizationId,
                stageId: stageRowIdRef.current,
                reportId: String(newRid),
              }),
            });
          } catch (linkErr) {
            console.error("welcomeStageLinkReport:", linkErr);
          }
        }
      } catch (error) {
        console.error("Report sync error:", error);
      } finally {
        sessionReadyToPersistRef.current = true;
      }
    };

    syncReportAndMaybeRedirect();
  }, [adminToken, isDemoBypass, navigate, questionTimerSec, perQuestionTimer, startQuestionTimer]);

  const getSessionTimeString = useCallback(() => {
    if (perQuestionTimer) {
      return formatSecondsToClock(sessionTimeSecRef.current);
    }
    return formatSecondsToClock(elapsedTimeRef.current);
  }, [perQuestionTimer]);

  /** One full theme timer (e.g. 01:00) per completed question — matches question_count × timer. */
  const creditCurrentQuestionTime = useCallback(() => {
    if (!perQuestionTimer || questionTimerSec <= 0) return;
    questionsCreditedRef.current += 1;
    sessionTimeSecRef.current = questionsCreditedRef.current * questionTimerSec;
  }, [perQuestionTimer, questionTimerSec]);

  const formatTimerDisplay = () => {
    if (perQuestionTimer) return String(Math.max(0, timeLeft));
    return String(elapsedTime);
  };

  const persistWelcomeStageProgress = async (completedQuestionCount) => {
    if (isDemoBypass) return;
    const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase || !stageRowIdRef.current || !uid || !sessionId || !organizationId || !adminToken) return;

    const timeStr = getSessionTimeString();

    try {
      await fetch(`${backendBase}/welcomeStageProgress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid,
          sessionId,
          organizationId,
          stageId: stageRowIdRef.current,
          currentQues: completedQuestionCount,
          total_score: pointsRef.current,
          wrongcount: wrongCountRef.current,
          time: timeStr,
        }),
      });
    } catch (e) {
      console.error("welcomeStageProgress:", e);
    }
  };

  const triggerShake = () => {
    setScreenShake(true);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    shakeTimerRef.current = setTimeout(() => {
      setScreenShake(false);
      shakeTimerRef.current = null;
    }, SHAKE_MS);
  };

  const triggerPointsGlow = () => {
    if (pointsGlowTimerRef.current) clearTimeout(pointsGlowTimerRef.current);
    setPointsGlowing(false);
    requestAnimationFrame(() => {
      setPointsGlowing(true);
      pointsGlowTimerRef.current = setTimeout(() => {
        setPointsGlowing(false);
        pointsGlowTimerRef.current = null;
      }, POINTS_GLOW_MS);
    });
  };

  const triggerDemoPlansRedirect = () => {
    if (!isDemoBypass || demoRedirectTriggeredRef.current) return;
    demoRedirectTriggeredRef.current = true;
    window.setTimeout(() => {
      void Swal.fire({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.assign("https://extramileplay.com/plans");
      });
    }, 400);
  };

  const completeGame = useCallback(async () => {
    if (gameCompleteRef.current) return;
    const totalQuestions = questionsListLengthRef.current;
    if (perQuestionTimer && totalQuestions > 0 && questionsCreditedRef.current < totalQuestions) {
      creditCurrentQuestionTime();
    }
    gameCompleteRef.current = true;
    setGameComplete(true);

    const currentPoints = pointsRef.current;
    const finishedTime = getSessionTimeString();

    try {
      if (!isDemoBypass) {
        const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
        const sessionId = storedUserData.sessionId;
        const organizationId = storedUserData.organizationId;
        const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

        if (backendBase && stageRowIdRef.current && uid && sessionId && organizationId && adminToken) {
          try {
            await fetch(`${backendBase}/welcomeStageComplete`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId,
                organizationId,
                stageId: stageRowIdRef.current,
                time: finishedTime,
                total_score: currentPoints,
                wrongcount: wrongCountRef.current,
                currentQues: questionsList.length,
              }),
            });
          } catch (stageErr) {
            console.error("welcomeStageComplete:", stageErr);
          }
        }

        await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId,
          organizationId,
          role: reportRoleForApi,
          token: storedUserData.token || "",
          points: currentPoints,
          time: finishedTime,
          gameId: storedUserData.gameId,
          name: normalizePlayerName(storedUserData),
          userId: uid,
        });
      }
    } catch (err) {
      console.error("Score update failed:", err);
    }

    setTimeout(() => {
      navigate("/leaderboard", { replace: true });
    }, 800);
  }, [
    isDemoBypass,
    navigate,
    reportRoleForApi,
    storedUserData,
    adminToken,
    getSessionTimeString,
    perQuestionTimer,
    creditCurrentQuestionTime,
  ]);

  const advanceQuestionOrFinish = useCallback(() => {
    if (gameCompleteRef.current) return;
    if (advanceInFlightRef.current) return;
    advanceInFlightRef.current = true;
    stopQuestionTimer();

    if (questionAdvanceTimerRef.current) {
      clearTimeout(questionAdvanceTimerRef.current);
      questionAdvanceTimerRef.current = null;
    }
    if (questionAdvanceFromZeroTimerRef.current) {
      clearTimeout(questionAdvanceFromZeroTimerRef.current);
      questionAdvanceFromZeroTimerRef.current = null;
    }

    const idx = activeQuestionIndexRef.current;
    const total = questionsListLengthRef.current;
    if (total <= 0) {
      advanceInFlightRef.current = false;
      return;
    }

    if (questionsCreditedRef.current <= idx) {
      creditCurrentQuestionTime();
    }
    if (idx >= total - 1) {
      advanceInFlightRef.current = false;
      completeGame();
      return;
    }
    persistWelcomeStageProgress(idx + 1);
    setActiveQuestionIndex(idx + 1);
  }, [completeGame, creditCurrentQuestionTime, stopQuestionTimer]);

  useEffect(() => {
    advanceQuestionOrFinishRef.current = advanceQuestionOrFinish;
  }, [advanceQuestionOrFinish]);

  const matchInWordList = (typed, words) => {
    const key = normalizeWordKey(typed);
    if (!key) return null;
    return words.find((w) => normalizeWordKey(w) === key) || null;
  };

  const validateWithAI = async (typed, question, alreadyFoundTexts) => {
    try {
      const raw = await callAI(
        buildWordValidatePrompt(
          question.topic_name,
          question.question_name,
          question.words,
          typed,
          alreadyFoundTexts
        ),
        adminToken
      );
      return parseAiYesNo(raw);
    } catch {
      return false;
    }
  };

  const commitWordToCloud = useCallback((wordEntry) => {
    setFoundWords((prev) => {
      if (prev.some((w) => w.key === wordEntry.key)) return prev;
      return [...prev, { ...wordEntry, skipEnter: true }];
    });
  }, []);

  useEffect(() => {
    flyingWordRef.current = flyingWord;
  }, [flyingWord]);

  useEffect(() => {
    if (!flyingWord || flyingWord.phase !== "start") return undefined;
    let innerId = 0;
    const outerId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => {
        setFlyingWord((f) => (f && f.phase === "start" ? { ...f, phase: "fly" } : f));
      });
    });
    return () => {
      cancelAnimationFrame(outerId);
      cancelAnimationFrame(innerId);
    };
  }, [flyingWord?.id, flyingWord?.phase]);

  const handleFlyTransitionEnd = useCallback(
    (e) => {
      if (e.propertyName !== "left" && e.propertyName !== "top") return;
      const fly = flyingWordRef.current;
      if (!fly || fly.phase !== "fly") return;
      if (fly.questionIndex !== activeQuestionIndexRef.current) {
        setPoints((p) => {
          const next = Math.max(0, p - (fly.earned || 0));
          pointsRef.current = next;
          return next;
        });
        flyingWordRef.current = null;
        setFlyingWord(null);
        return;
      }
      flyingWordRef.current = null;
      setFlyingWord(null);
      const { slot, key, text, color, id } = fly;
      commitWordToCloud({
        id,
        key,
        text,
        color,
        left: slot.left,
        top: slot.top,
        rot: slot.rot,
        scale: slot.scale,
      });
    },
    [commitWordToCloud]
  );

  const handleCorrectAnswer = (displayText) => {
    const qLocked = activeQuestionIndexRef.current;
    const key = normalizeWordKey(displayText);
    if (foundWords.some((w) => w.key === key)) return;

    const slotIndex = foundWords.length;
    const slot = CLOUD_SLOTS[slotIndex % CLOUD_SLOTS.length];
    const color = CLOUD_COLORS[slotIndex % CLOUD_COLORS.length];
    const id = `${key}-${Date.now()}`;

    const earned = computeAnswerPoints(displayText, adminRightPoints);
    setPoints((p) => {
      const next = p + earned;
      pointsRef.current = next;
      return next;
    });
    triggerPointsGlow();
    if (slotIndex === 0) {
      triggerDemoPlansRedirect();
    }
    setInputValue("");

    const inputEl = inputRef.current;
    const cloudEl = cloudRef.current;
    if (!inputEl || !cloudEl) {
      if (activeQuestionIndexRef.current !== qLocked) {
        setPoints((p) => {
          const next = Math.max(0, p - earned);
          pointsRef.current = next;
          return next;
        });
        return;
      }
      commitWordToCloud({
        id,
        key,
        text: displayText,
        color,
        left: slot.left,
        top: slot.top,
        rot: slot.rot,
        scale: slot.scale,
      });
      scheduleAnswerInputFocus();
      return;
    }

    const inputRect = inputEl.getBoundingClientRect();
    const cloudRect = cloudEl.getBoundingClientRect();
    const fromX = inputRect.left + inputRect.width / 2;
    const fromY = inputRect.top + inputRect.height / 2;
    const toX = cloudRect.left + (slot.left / 100) * cloudRect.width;
    const toY = cloudRect.top + (slot.top / 100) * cloudRect.height;

    setFlyingWord({
      id: `fly-${id}`,
      key,
      text: displayText,
      color,
      slot,
      questionIndex: qLocked,
      earned,
      fromX,
      fromY,
      toX,
      toY,
      phase: "start",
    });
  };

  const handleWrongAnswer = () => {
    triggerShake();
    setWrongCount((w) => {
      const next = w + 1;
      wrongCountRef.current = next;
      return next;
    });
    setInputValue("");
    scheduleAnswerInputFocus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (gameComplete || isValidating || flyingWord || !activeQuestion) return;

    const typed = inputValue.trim();
    if (!typed) return;

    const alreadyFound = foundWords.some((w) => w.key === normalizeWordKey(typed));
    if (alreadyFound) {
      handleWrongAnswer();
      return;
    }

    const qIndexAtSubmit = activeQuestionIndexRef.current;
    const questionSnapshot = activeQuestion;
    const alreadyFoundTexts = foundWords.map((w) => w.text);

    const directMatch = matchInWordList(typed, activeQuestion.words);
    if (directMatch) {
      if (activeQuestionIndexRef.current !== qIndexAtSubmit) return;
      handleCorrectAnswer(directMatch);
      return;
    }

    if (isLikelyMisspellingOfListWord(typed, activeQuestion.words)) {
      handleWrongAnswer();
      return;
    }

    if (answerLooksInvalid(typed)) {
      handleWrongAnswer();
      return;
    }

    setIsValidating(true);
    try {
      const aiOk = await validateWithAI(typed, questionSnapshot, alreadyFoundTexts);
      if (activeQuestionIndexRef.current !== qIndexAtSubmit) {
        return;
      }
      if (aiOk) {
        handleCorrectAnswer(typed);
      } else {
        handleWrongAnswer();
      }
    } finally {
      setIsValidating(false);
      scheduleAnswerInputFocus();
    }
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !themeData) {
    return (
      <div className="fullhouse-page">
        <div className="fullhouse-loader" />
      </div>
    );
  }

  if (hasNoQuestions) {
    return <div className="fullhouse-page" />;
  }

  const viewBgDesktop = getDvSourceImageUrl(themeData.background_desk);
  const viewBgMobile = getDvSourceImageUrl(themeData.background_mob);
  const rulesList = ensureThemeJsonArray(themeData?.rules).filter(
    (r) => typeof r === "string" && r.trim()
  );

  const landingTitleColor =
    themeColorToCss(themeData?.landing_page_title_color ?? themeData?.landing_page_title_colour) ||
    "#000000";

  const gameTextColor =
    themeColorToCss(themeData?.game_text_color ?? themeData?.game_text_colour) || "#ffffff";

  const hudStyle = {
    ...(themeData?.button_color ? { "--welcome-hud-bg": themeData.button_color } : null),
    ...(themeData?.button_Textcolor ? { "--welcome-hud-text": themeData.button_Textcolor } : null),
    "--welcome-game-text-color": gameTextColor,
  };

  return (
    <div
      className={`category-rush-game game-container${screenShake ? " category-rush-game--shake" : ""}`}
      style={hudStyle}
    >
      {viewBgDesktop || viewBgMobile ? (
        <div
          className="category-rush-bg-image"
          aria-hidden="true"
          
        />
      ) : null}

      <header className="category-rush-hud game-hud-bar game-hud-bar--welcome" aria-label="Game status">
        <div className="category-rush-hud-left">
          <button
            type="button"
            className="category-rush-help-btn"
            onClick={() => setShowRulesPopup(true)}
            aria-label="How to play"
          >
            ?
          </button>
        </div>

        <div
          className="category-rush-hud-center"
          style={
            perQuestionTimer
              ? { "--timer-line-progress": String(timerLineProgress) }
              : undefined
          }
        >
          <div className="category-rush-timer-rail">
            <span
              className="category-rush-timer-segment category-rush-timer-segment--left"
              aria-hidden="true"
            />
            <span className="category-rush-timer-box game-hud-pocket game-hud-pocket--timer">
              {formatTimerDisplay()}
            </span>
            <span
              className="category-rush-timer-segment category-rush-timer-segment--right"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="category-rush-hud-right game-hud-points game-hud-points--welcome">
          <div
            className={`category-rush-points-btn${pointsGlowing ? " category-rush-points-btn--glow" : ""}`}
            aria-label={`Points: ${points}`}
          >
            <img src={pointIcon} alt="" className="category-rush-points-btn-icon" width={22} height={22} />
            <span className="category-rush-points-btn-value">{points}</span>
          </div>
        </div>
      </header>

      <main className="category-rush-main">
        <div className="category-rush-question-wrap">
          <div className="category-rush-question-badge" aria-hidden="true">
            ?
          </div>
          <div className="category-rush-question-card">
            <p className="category-rush-question-text" style={{ color: landingTitleColor }}>
              {activeQuestion?.question_name || ""}
            </p>
          </div>
        </div>

        <div className="category-rush-cloud" ref={cloudRef} aria-live="polite">
          {foundWords.map((w) => (
            <span
              key={w.id}
              className={`category-rush-cloud-word${w.skipEnter ? "" : " category-rush-cloud-word--enter"}`}
              style={{
                left: `${w.left}%`,
                top: `${w.top}%`,
                color: w.color,
                transform: `translate(-50%, -50%) rotate(${w.rot}deg) scale(${w.scale})`,
              }}
            >
              {w.text}
            </span>
          ))}
        </div>

        {flyingWord ? (
          <span
            className={`category-rush-word-fly${flyingWord.phase === "fly" ? " category-rush-word-fly--active" : ""}`}
            style={{
              left: flyingWord.phase === "fly" ? flyingWord.toX : flyingWord.fromX,
              top: flyingWord.phase === "fly" ? flyingWord.toY : flyingWord.fromY,
              color: flyingWord.color,
              "--fly-color": flyingWord.color,
              "--fly-rot": `${flyingWord.slot.rot}deg`,
              "--fly-scale": flyingWord.slot.scale,
            }}
            onTransitionEnd={handleFlyTransitionEnd}
          >
            {flyingWord.text}
          </span>
        ) : null}

        <form
          className="category-rush-input-form"
          onSubmit={handleSubmit}
          style={{ "--welcome-input-text-color": landingTitleColor }}
        >
          <input
            ref={inputRef}
            type="text"
            className="category-rush-input"
            placeholder="Type something...."
            style={{ color: landingTitleColor }}
            value={inputValue}
            onChange={(ev) => setInputValue(ev.target.value.slice(0, MAX_PLAYER_ANSWER_LEN))}
            disabled={gameComplete || isValidating || !!flyingWord}
            maxLength={MAX_PLAYER_ANSWER_LEN}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="send"
            aria-label="Enter your answer"
          />
          {!isValidating ? (
            <button
              type="submit"
              className="category-rush-submit-arrow"
              style={{
                color: landingTitleColor,
                borderColor: landingTitleColor,
              }}
              disabled={gameComplete || !!flyingWord || !inputValue.trim()}
              aria-label="Submit answer"
            >
              <svg
                className="category-rush-submit-arrow-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M4 12v3h9.586l-2.293 2.293 1.414 1.414L18.414 12l-5.707-5.707-1.414 1.414L13.586 9H4v3z"
                />
              </svg>
            </button>
          ) : null}
          {isValidating ? (
            <span
              className="category-rush-validating"
              style={{ color: landingTitleColor }}
              aria-hidden="true"
            >
              Checking…
            </span>
          ) : null}
        </form>
      </main>

      {showRulesPopup ? (
        <div className="welcome-overlay-backdrop" role="presentation">
          <div className="welcome-rules-card" role="dialog" aria-modal="true" aria-label="How to play">
            <h3 style={{ color: landingTitleColor }}>HOW TO PLAY</h3>
            <div className="welcome-rules-list">
              {(rulesList.length ? rulesList : ["Read the question and type correct words."]).map(
                (rule, idx) => (
                  <div key={`rule-${idx}`} className="welcome-rule-item">
                    <span className="welcome-rule-box" style={{ color: landingTitleColor }}>☐</span>
                    <span style={{ color: landingTitleColor }}>{rule}</span>
                  </div>
                )
              )}
            </div>
            <button
              type="button"
              className="welcome-rules-close-btn"
              onClick={() => setShowRulesPopup(false)}
              style={{
                ...(themeData?.button_color ? { background: themeData.button_color } : null),
                ...(themeData?.button_Textcolor ? { color: themeData.button_Textcolor } : null),
              }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Welcome;
